import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Settings as SettingsIcon, Building2, Upload, Loader2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      if (res.data.logo_url) setLogoPreview(res.data.logo_url);
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      if (settings.company_name) formData.append("company_name", settings.company_name);
      if (settings.address) formData.append("address", settings.address);
      if (settings.email) formData.append("email", settings.email);
      if (settings.phone) formData.append("phone", settings.phone);
      if (settings.gst_number) formData.append("gst_number", settings.gst_number);
      if (settings.pan_number) formData.append("pan_number", settings.pan_number);
      if (settings.bank_details) formData.append("bank_details", settings.bank_details);
      if (logoFile) formData.append("logo", logoFile);

      await axios.put(`${API}/settings`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Settings saved successfully");
      fetchData();
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <SettingsIcon className="w-6 h-6 text-slate-600" /> Settings
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage company information</p>
        </div>
        <Button onClick={handleSaveSettings} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      {/* Company Logo */}
      <Card className="mb-5" data-testid="company-logo-card">
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Building2 className="w-4 h-4 text-indigo-600" /> Company Logo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <Upload className="w-6 h-6 text-slate-300" />
              )}
            </div>
            <div>
              <Label htmlFor="logo-upload" className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-700 transition-colors">
                <Upload className="w-3.5 h-3.5" /> Upload Logo
              </Label>
              <input id="logo-upload" type="file" accept="image/*" onChange={handleLogoChange} className="hidden" data-testid="logo-upload-input" />
              <p className="text-[10px] text-slate-400 mt-1">PNG, JPG up to 2MB</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Information */}
      <Card data-testid="company-info-card">
        <CardHeader className="py-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Building2 className="w-4 h-4 text-teal-600" /> Company Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Company Name</Label>
              <Input value={settings?.company_name || ""} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })} placeholder="Graphikpro" data-testid="company-name-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email</Label>
              <Input value={settings?.email || ""} onChange={(e) => setSettings({ ...settings, email: e.target.value })} placeholder="info@company.com" data-testid="company-email-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Phone</Label>
              <Input value={settings?.phone || ""} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} placeholder="+91 12345 67890" data-testid="company-phone-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">GST Number</Label>
              <Input value={settings?.gst_number || ""} onChange={(e) => setSettings({ ...settings, gst_number: e.target.value })} placeholder="22AAAAA0000A1Z5" data-testid="company-gst-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">PAN Number</Label>
              <Input value={settings?.pan_number || ""} onChange={(e) => setSettings({ ...settings, pan_number: e.target.value })} placeholder="ABCDE1234F" data-testid="company-pan-input" />
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-xs text-slate-500">Address</Label>
            <Textarea value={settings?.address || ""} onChange={(e) => setSettings({ ...settings, address: e.target.value })} placeholder="Company address" rows={2} data-testid="company-address-input" />
          </div>
          <div className="mt-4">
            <Label className="text-xs text-slate-500">Bank Details</Label>
            <Textarea value={settings?.bank_details || ""} onChange={(e) => setSettings({ ...settings, bank_details: e.target.value })} placeholder="Bank name, account number, IFSC..." rows={3} data-testid="company-bank-input" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
